// ─── Lighting Manager ─────────────────────────────────────────────────────────
// Manages lighting configuration, dependencies, constraints, and positions.
// Follows R-Blade Gate 6 chatflow logic exactly.

export const LIGHT_TYPES = {
  recessed: {
    label: 'Recessed Lights',
    description: '2 per bay max, flank the fan in center',
    maxPerBay: 2,
    requiresFanBeam: false,
    icon: '💡',
  },
  ledBar: {
    label: 'LED Bar / Ramp Lights',
    description: '4 per bay, mounted on beams',
    defaultPerBay: 4,
    conflictsWith: ['ledStrip'],
    icon: '▬',
  },
  ledStrip: {
    label: 'LED Strip Lights',
    description: 'Perimeter ambient light, color-changing',
    pricedPerFoot: true,
    requiresControlBox: true,
    conflictsWith: ['ledBar'],
    icon: '🌈',
  },
  solarPost: {
    label: 'Solar Post Lights',
    description: 'Up to 4 per post, illuminate downward',
    maxPerPost: 4,
    icon: '☀',
  },
  ceilingFan: {
    label: 'Ceiling Fan',
    description: '1 per bay, requires fan beam',
    requiresFanBeam: true,
    maxPerBay: 1,
    icon: '🌀',
  },
  controlBox: {
    label: 'Control Box / Driver',
    description: 'Required for LED strip lights, 1 per bay',
    perBay: true,
    icon: '🔌',
  },
};

// Lights & Fan Package contents (per bay)
export const LIGHTS_FAN_PACKAGE = {
  label: 'Lights & Fan Package',
  description: '2 Recessed + 4 LED Bars + 1 Fan + 1 Fan Beam per bay',
  contents: {
    recessed: 2,
    ledBar: 4,
    ceilingFan: 1,
    fanBeam: 1,  // auto-adds accessory beam
  },
};

export class LightingManager {
  constructor() {
    this.config = {
      recessed: 0,
      ledBar: 0,
      ledStrip: false,
      ledStripColorHue: 0,     // 0-360 hue for strip color
      ledStripBrightness: 0.8,
      solarPostLights: 0,      // per post
      solarPostSide: 'all',    // 'all', 'front', 'back', 'left', 'right'
      ceilingFan: 0,
      controlBox: 0,
      lightsAndFanPackage: 0,  // number of packages (typically = total bays)
    };
    this.totalBays = 1;
    this.postCount = 4;
    this.warnings = [];
    this.errors = [];
  }

  // Update context from pricing
  setContext(totalBays, postCount) {
    this.totalBays = totalBays;
    this.postCount = postCount;
  }

  // ─── Configuration setters ───────────────────────────────────────
  setRecessed(qty) {
    this.config.recessed = Math.min(qty, this.totalBays * 2);
    this.validate();
  }

  setLedBars(qty) {
    this.config.ledBar = qty;
    this.validate();
  }

  setLedStrip(enabled) {
    this.config.ledStrip = enabled;
    this.validate();
  }

  setLedStripColor(hue) {
    this.config.ledStripColorHue = Math.max(0, Math.min(360, hue));
  }

  setLedStripBrightness(brightness) {
    this.config.ledStripBrightness = Math.max(0, Math.min(1, brightness));
  }

  setSolarPostLights(perPost) {
    this.config.solarPostLights = Math.min(perPost, 4);
    this.validate();
  }

  setCeilingFan(qty) {
    this.config.ceilingFan = Math.min(qty, this.totalBays);
    this.validate();
  }

  setLightsAndFanPackage(qty) {
    this.config.lightsAndFanPackage = qty;
    this.validate();
  }

  // ─── Validation / Dependency enforcement ─────────────────────────
  validate() {
    this.warnings = [];
    this.errors = [];

    // LED Bar vs LED Strip conflict
    if (this.config.ledBar > 0 && this.config.ledStrip) {
      this.errors.push('LED Bar/Ramp lights cannot be combined with LED Strip lights. Choose one.');
    }

    // LED Strip requires control box
    if (this.config.ledStrip && this.config.controlBox < this.totalBays) {
      this.warnings.push(`LED Strip lights require ${this.totalBays} Control Box(es) — auto-adding.`);
      this.config.controlBox = this.totalBays;
    }

    // No LED strip → no forced control box
    if (!this.config.ledStrip && this.config.controlBox > 0) {
      // Allow manual control box if they want, but warn
    }

    // Ceiling fan requires fan beam (handled by beam manager, but note it)
    if (this.config.ceilingFan > 0) {
      this.warnings.push('Ceiling fan(s) require a Fan Beam — ensure one is added per fan.');
    }

    // Package check: don't double-count
    if (this.config.lightsAndFanPackage > 0) {
      const pkgRecessed = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.recessed;
      const pkgLedBar = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.ledBar;
      const pkgFans = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.ceilingFan;

      if (this.config.recessed > 0 && this.config.recessed <= pkgRecessed) {
        this.warnings.push('Recessed lights are included in your Lights & Fan Package.');
      }
      if (this.config.ledBar > 0 && this.config.ledBar <= pkgLedBar) {
        this.warnings.push('LED Bars are included in your Lights & Fan Package.');
      }
      if (this.config.ceilingFan > 0 && this.config.ceilingFan <= pkgFans) {
        this.warnings.push('Ceiling fan is included in your Lights & Fan Package.');
      }
    }

    return { warnings: this.warnings, errors: this.errors };
  }

  hasConflict() {
    return this.errors.length > 0;
  }

  // ─── Effective counts (accounting for packages) ──────────────────
  getEffectiveCounts() {
    const pkg = this.config.lightsAndFanPackage;
    const pkgContents = LIGHTS_FAN_PACKAGE.contents;

    return {
      recessed: Math.max(this.config.recessed, pkg * pkgContents.recessed),
      ledBar: this.config.ledStrip ? 0 : Math.max(this.config.ledBar, pkg * pkgContents.ledBar),
      ledStrip: this.config.ledStrip,
      ledStripColorHue: this.config.ledStripColorHue,
      ledStripBrightness: this.config.ledStripBrightness,
      solarPostLights: this.config.solarPostLights,
      ceilingFan: Math.max(this.config.ceilingFan, pkg * pkgContents.ceilingFan),
      controlBox: this.config.controlBox,
      fanBeamsNeeded: Math.max(this.config.ceilingFan, pkg * pkgContents.ceilingFan),
      totalSolarLights: this.config.solarPostLights * this.postCount,
    };
  }

  // ─── LED Strip perimeter calculation ─────────────────────────────
  getLedStripFootage(widthInches, lengthInches) {
    if (!this.config.ledStrip) return 0;
    const perimeterInches = (widthInches + lengthInches) * 2;
    return Math.ceil(perimeterInches / 12); // feet
  }

  // ─── For pricing ─────────────────────────────────────────────────
  getLightingLineItems(widthInches, lengthInches) {
    const effective = this.getEffectiveCounts();
    const items = [];

    if (this.config.lightsAndFanPackage > 0) {
      items.push({
        item: 'Lights & Fan Package',
        qty: this.config.lightsAndFanPackage,
        perBay: true,
        sku: 'LFP-BAY',
        unitPrice: 0,
      });
    }

    // Only add individual items if they exceed package amounts
    const pkgRecessed = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.recessed;
    const extraRecessed = Math.max(0, this.config.recessed - pkgRecessed);
    if (extraRecessed > 0) {
      items.push({ item: 'Recessed Light', qty: extraRecessed, sku: 'LIGHT-RECESS', unitPrice: 0 });
    }

    const pkgLedBar = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.ledBar;
    const extraLedBar = Math.max(0, this.config.ledBar - pkgLedBar);
    if (extraLedBar > 0) {
      items.push({ item: 'LED Bar / Ramp Light', qty: extraLedBar, sku: 'LIGHT-LEDBAR', unitPrice: 0 });
    }

    if (this.config.ledStrip) {
      const footage = this.getLedStripFootage(widthInches, lengthInches);
      items.push({ item: 'LED Strip Light', qty: footage, unit: 'ft', sku: 'LIGHT-STRIP', unitPrice: 0 });
    }

    if (this.config.controlBox > 0) {
      items.push({ item: 'Control Box / Driver', qty: this.config.controlBox, sku: 'CTRL-BOX', unitPrice: 0 });
    }

    if (effective.totalSolarLights > 0) {
      items.push({ item: 'Solar Post Light', qty: effective.totalSolarLights, sku: 'LIGHT-SOLAR', unitPrice: 0 });
    }

    const pkgFans = this.config.lightsAndFanPackage * LIGHTS_FAN_PACKAGE.contents.ceilingFan;
    const extraFans = Math.max(0, this.config.ceilingFan - pkgFans);
    if (extraFans > 0) {
      items.push({ item: 'Ceiling Fan', qty: extraFans, sku: 'FAN-CEILING', unitPrice: 0 });
    }

    return items;
  }

  getConfig() {
    return { ...this.config };
  }
}
